import { observer } from 'mobx-react-lite'
import FloatingSave from 'app/components/FloatingSave'
import FormComboAsyncV2 from 'app/components/form/FormComboAsyncV2'
import FormInput from 'app/components/form/FormInput'
import FormPanel from 'app/components/form/FormPanel'
import { SmartForm } from 'app/components/form/SmartForm'
import { LearnMoreLink } from 'app/components/shared/LearnMoreLink'
import UserLocationSelector from 'app/components/UserLocationSelector'
import { RoleSelector } from 'app/components/users/RoleSelector'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { legacyRoleRank } from 'app/types/roles'
import { StaffJson } from 'app/types/entities'
import { debug } from 'lib/logging'
import type { UseUserWorkspaceReturn } from './useUserWorkspace'
import { userWorkspaceOtherValidator, userWorkspaceSelfValidator } from './userWorkspaceValidator'
import { createGqlFetcher } from 'app/lib/gqlFetcher'

export type UserWorkspaceComponentProps = UseUserWorkspaceReturn

export const UserWorkspaceComponent = observer(function UserWorkspaceComponent(props: UserWorkspaceComponentProps) {
  const {
    me,
    user,
    userId,
    currentWorkspace,
    isViewingSelf,
    canEdit,
    canRemove,
    workspaceRoles,
    isDirtyForm,
    setIsDirtyForm,
    role,
    setRole,
    roleId,
    setRoleId,
    selectedStaff,
    setSelectedStaff,
    locationInputs,
    setLocationInputs,
    removeUserHandler,
    isDeleting,
    onSave,
  } = props

  return (
    <SmartForm
      id={`workspace-form-${userId}`}
      validator={isViewingSelf ? userWorkspaceSelfValidator : userWorkspaceOtherValidator}
      method="post"
      preventSubmit
      onSubmit={(data) => {
        if (data) onSave(data)
      }}
      className="!mt-0 gap-y-8"
      hiddenInputs={[
        { name: `id`, value: user.id },
        { name: `orgId`, value: me?.orgId },
        { name: `workspaceId`, value: currentWorkspace?.id! },
        { name: `roleId`, value: roleId },
        ...locationInputs,
      ]}
      hideButtons={true}
    >
      {selectedStaff && <input type="hidden" name="staffId" value={selectedStaff.id} />}

      <FormPanel title="Profile Details" cols={6}>
        <div className="flex flex-col gap-4">
          <FormInput
            name="name"
            label="Name"
            labelClassName={`text-ui-sm font-semibold text-strong ${isViewingSelf ? `required` : ``}`}
            className="text-body-sm text-strong shadow-elevation-1"
            defaultValue={user.name}
            disabled={!canEdit}
            onChange={() => setIsDirtyForm(true)}
          />
          <FormInput
            name="email"
            label="Email"
            type="email"
            required
            labelClassName="text-ui-sm required font-semibold text-strong"
            className="text-body-sm text-strong shadow-elevation-1"
            defaultValue={user.email}
            disabled={!canEdit}
            onChange={() => setIsDirtyForm(true)}
          />
          <FormInput
            name="phone"
            label="Phone Number"
            labelClassName="text-ui-sm font-semibold text-strong"
            className="text-body-sm text-strong shadow-elevation-1"
            defaultValue={formatPhoneNumber(user.phone ?? ``)}
            disabled={!canEdit}
            onChange={() => setIsDirtyForm(true)}
          />
        </div>
      </FormPanel>
      <FormPanel
        title="Permissions"
        cols={6}
        learnMore={
          <LearnMoreLink to="https://intercom.help/trackable/en/articles/11643458-user-permissions">
            Learn About Permissions
          </LearnMoreLink>
        }
      >
        <RoleSelector
          workspaceRoles={workspaceRoles}
          role={role}
          roleId={roleId}
          onChange={(newRole, newRoleId) => {
            if (newRole !== undefined) setRole(newRole)
            if (newRoleId !== undefined) setRoleId(newRoleId)
          }}
          setIsDirtyForm={setIsDirtyForm}
          disabled={!canEdit || isViewingSelf}
        />
        <UserLocationSelector
          user={user}
          role={user.role ?? legacyRoleRank.workspaceUser}
          isNewUser={false}
          isNewWorkspaceUser={false}
          isOrgLevel={false}
          staffV2Enabled={true}
          setIsDirtyForm={setIsDirtyForm}
          onHiddenInputsChange={setLocationInputs}
          disabled={!canEdit || isViewingSelf}
        />
      </FormPanel>
      <FormPanel
        title="Linked Staff"
        cols={6}
        learnMore={
          <LearnMoreLink to="https://intercom.help/trackable/en/articles/11643458-user-permissions">
            Learn About Staff
          </LearnMoreLink>
        }
      >
        <div className="col-span-6">
          <FormComboAsyncV2
            name="_staffDisplay"
            label="Linked Staff Member"
            labelClassName="text-ui-sm text-default"
            inputClassName="shadow-elevation-1 text-body-sm text-strong"
            optionsMapper={(res) =>
              res.hits.map((hit: StaffJson) => ({ id: hit.id!, name: hit.name!, value: hit.id! }))
            }
            fetcher={createGqlFetcher('users')}
            additionalParams={{ unassignedOnly: `true`, onlyUsers: `true`, workspaceId: user.workspaceId ?? `` }}
            value={selectedStaff}
            onChange={(option) => {
              debug(`staff`, `onChange`, option)
              if (selectedStaff && option && selectedStaff.id === option.id) {
                setSelectedStaff(undefined)
              } else if (!option || option.id === `-1`) {
                setSelectedStaff(undefined)
              } else {
                setSelectedStaff(option)
              }
              setIsDirtyForm(true)
            }}
            placeholder="No Linked Staff"
            disabled={!canEdit}
          />
        </div>
      </FormPanel>

      {canRemove && (
        <FormPanel title="Manage Access" cols={6}>
          <div>
            <button
              type="button"
              className="rounded-md border border-red-600 px-4 py-2 text-label-xs font-semibold text-red-600 hover:bg-red-50"
              onClick={removeUserHandler}
              disabled={isDeleting}
            >
              {isDeleting ? `Removing...` : `Remove User`}
            </button>
          </div>
        </FormPanel>
      )}

      {canEdit && <FloatingSave isDirtyForm={isDirtyForm} buttonLabel="Save Changes" />}
    </SmartForm>
  )
})
